import React, { useMemo } from 'react';
import './GroupMailRulesTab.css';
import Togglebox from '../../input/Togglebox';
import ActionButton from '../../input/ActionButton';
import { mailSlotLabel } from '../../../util/mailSlots';

// Guest never has memberships → the welcome mail can never be sent for it.
const hiddenForGuest = (slot) => slot === 'welcome';

// No code default anymore: a rule without textblock sends nothing. Text per row, null = no warning.
function missingTextblockWarning(rule) {
  if (rule.textblockId != null) return null;
  if (rule.slot === 'announcement') {
    return 'Mail-Schritte ohne eigenen Textbaustein können an diese Gruppe nicht senden, solange hier keiner gewählt ist.';
  }
  if (rule.isExplicit) return 'Kann nicht gesendet werden, solange kein Textbaustein gewählt ist.';
  return rule.enabled ? 'Ohne Textbaustein wird keine Mail gesendet.' : null;
}

/**
 * Tab "Mails": one row per mail slot — on/off (automatic slots only) + textblock (null = no mail).
 * Rules are loaded by the editor (GET {id}/MailRules) and saved together with "Speichern".
 */
function GroupMailRulesTab({ group, isCreate, copySource, rulesStatus, draft, onChangeRule, onRetry, textblocks, isOffline }) {
  const list = useMemo(() => textblocks?.list ?? [], [textblocks]);
  const sorted = useMemo(
    () => [...list].sort((a, b) => (a.frontendName || a.name).localeCompare(b.frontendName || b.name, 'de')),
    [list]);
  const knownIds = useMemo(() => new Set(list.map(t => t.id)), [list]);

  if (isCreate) {
    return (
      <div className="groupTabBody">
        <p className="groupEditorExplanation">
          {copySource
            ? `Die neue Gruppe übernimmt die Mail-Regeln von „${copySource.name}“.`
            : 'Die neue Gruppe startet ohne Textbausteine – bis du hier welche wählst, bekommt sie keine Mails.'}
          {' '}Nach dem Anlegen kannst du die Regeln hier anpassen.
        </p>
      </div>
    );
  }

  let content;
  if (rulesStatus === 'loaded' && draft) {
    const rows = draft.filter(r => !(group?.isGuest && hiddenForGuest(r.slot)));
    content = (
      <ul className="groupMailRows">
        {rows.map(rule => {
          const label = rule.slotLabel || mailSlotLabel(rule.slot);
          const value = rule.textblockId == null ? '' : String(rule.textblockId);
          const isOff = !rule.isExplicit && !rule.enabled;
          const warning = missingTextblockWarning(rule);
          return (
            <li key={rule.slot} className={`groupMailRow${isOff ? ' isOff' : ''}`}>
              <div className="groupMailRowInfo">
                <span className="groupMailRowLabel">{label}</span>
                {rule.isExplicit && <span className="groupMailRowExplicit">wird nur ausdrücklich versendet</span>}
              </div>
              <div className="groupMailRowSwitch">
                {!rule.isExplicit && (
                  <React.Fragment>
                    <span className="groupMailRowState" aria-hidden="true">{rule.enabled ? 'An' : 'Aus'}</span>
                    <Togglebox
                      checked={rule.enabled}
                      onChange={e => onChangeRule(rule.slot, { enabled: e.target.checked })}
                    />
                  </React.Fragment>
                )}
              </div>
              <div className="groupEditorSelectWrap groupMailRowSelectWrap">
                <select
                  className="groupEditorSelect groupMailRowSelect"
                  aria-label={`Textbaustein für „${label}“`}
                  value={value}
                  onChange={e => onChangeRule(rule.slot, { textblockId: e.target.value === '' ? null : Number(e.target.value) })}
                >
                  <option value="">Kein Textbaustein – keine Mail</option>
                  {rule.textblockId != null && !knownIds.has(rule.textblockId) && (
                    <option value={value}>Textbaustein #{rule.textblockId}</option>
                  )}
                  {sorted.map(t => <option key={t.id} value={String(t.id)}>{t.frontendName || t.name}</option>)}
                </select>
                <span className="material-symbols-rounded groupEditorSelectArrow" aria-hidden="true">expand_more</span>
              </div>
              {warning && (
                <p className="groupMailRowWarning">
                  <span className="material-symbols-rounded" aria-hidden="true">warning</span>
                  {warning}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    );
  } else if (rulesStatus === 'error') {
    content = (
      <div className="groupMailMessage">
        <p>Die Mail-Regeln konnten nicht geladen werden.</p>
        <ActionButton icon="refresh" label="Erneut laden" onClick={onRetry} disabled={isOffline} />
      </div>
    );
  } else if (isOffline) {
    content = <p className="groupMailMessage">Offline nicht verfügbar.</p>;
  } else {
    content = <p className="groupMailMessage">Lädt …</p>;
  }

  return (
    <div className="groupTabBody">
      <p className="groupEditorExplanation">
        Mail-Regeln gelten sofort nach dem Speichern – auch für laufende Prozesse und bereits angelegte Spieltage.
        Ohne Textbaustein wird die Mail nicht gesendet. Prozessvorlagen können einzelne Regeln überschreiben.
      </p>
      {textblocks?.status === 'error' && (
        <p className="formFieldHint">Textbausteine konnten nicht geladen werden – die Auswahl ist leer.</p>
      )}
      {content}
    </div>
  );
}

export default GroupMailRulesTab;
